import { showToast } from '@design-system';
import {
    HolidayState,
    HolidayToUpdate,
    HolidayType,
    Operator,
    useDeleteHolidayMutation,
    useGetHolidaysMutation,
    useGetHolidayTypesMutation,
    useGetUsersMutation,
    useUpdateHolidayMutation
} from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const useUpdateCongePage = () => {
    const { holidayId } = useParams();
    const [holiday, setHoliday] = useState<HolidayToUpdate>();
    const [originalHoliday, setOriginalHoliday] = useState<HolidayToUpdate>();
    const [holidayTypes, setHolidayTypes] = useState<HolidayType[]>([]);
    const [operators, setOperators] = useState<Operator[]>([]);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

    const [getHolidayTypes, { isLoading: isLoadingHolidayTypes, error: errorHolidayTypes, data: dataHolidayTypes }] =
        useGetHolidayTypesMutation();
    const [getUsers, { isLoading: isLoadingUsers, error: errorUsers, data: users }] = useGetUsersMutation();
    const [getHolidays, { isLoading, error, data }] = useGetHolidaysMutation();
    const [updateHoliday, { isLoading: isLoadingUpdateHoliday, error: errorUpdateHoliday, data: dataUpdateHoliday }] =
        useUpdateHolidayMutation();
    const [deleteHoliday, { error: errorDeleteHoliday, data: dataDeleteHoliday }] = useDeleteHolidayMutation();

    const navigate = useNavigate();

    const handleUpdateHoliday = (holiday: HolidayToUpdate) => {
        updateHoliday(holiday);
    };

    const updateHolidayToUpdate = (key: keyof HolidayToUpdate, value: string) => {
        if (holiday) {
            setHoliday({ ...holiday, [key]: value });
        }
    };

    const confirmDeleteHoliday = () => {
        if (holiday?.holiday_id) {
            deleteHoliday({ holiday_id: holiday.holiday_id });
        }
    };

    const isHolidayUpdated = useMemo(() => {
        if (!originalHoliday || !holiday) {
            return false;
        }

        let result = false;

        Object.entries(originalHoliday).forEach(([key, value]) => {
            if (holiday[key as keyof HolidayToUpdate] !== value) {
                result = true;
            }
        });

        return result;
    }, [originalHoliday, holiday]);

    useEffect(() => {
        getHolidays(undefined);
        getHolidayTypes(undefined);
        getUsers(undefined);
    }, [getHolidays, getHolidayTypes, getUsers]);

    useEffect(() => {
        if (data) {
            const foundHoliday = data.find((holiday) => holiday.id === holidayId);
            if (foundHoliday) {
                setHoliday({ holiday_id: foundHoliday.id, ...foundHoliday });
                setOriginalHoliday({ holiday_id: foundHoliday.id, ...foundHoliday });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (errorHolidayTypes) {
            console.error(errorHolidayTypes);
        }
        if (dataHolidayTypes) {
            setHolidayTypes(dataHolidayTypes);
        }
    }, [errorHolidayTypes, dataHolidayTypes]);

    useEffect(() => {
        if (errorUsers) {
            console.error(errorUsers);
        }
        if (users) {
            setOperators(users.operators);
        }
    }, [users, isLoadingUsers, errorUsers]);

    useEffect(() => {
        if (dataUpdateHoliday) {
            showToast({
                message: "Demande d'absence modifiée avec succès",
                type: 'success'
            });
            navigate('/richesses-humaines/conges');
        }
        if (errorUpdateHoliday) {
            console.error('errorUpdateHoliday:', errorUpdateHoliday);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataUpdateHoliday, errorUpdateHoliday]);

    useEffect(() => {
        if (dataDeleteHoliday) {
            showToast({
                message: "Demande d'absence supprimée avec succès",
                type: 'success'
            });
            navigate('/richesses-humaines/conges');
        }
        if (errorDeleteHoliday) {
            console.error('errorDeleteHoliday:', errorDeleteHoliday);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataDeleteHoliday, errorDeleteHoliday]);

    return {
        holiday,
        holidayTypes,
        isLoading: isLoadingHolidayTypes || isLoadingUsers || isLoadingUpdateHoliday || isLoading,
        error: errorHolidayTypes || errorUsers || errorUpdateHoliday || error,
        operators,
        handleUpdateHoliday,
        updateHolidayToUpdate,
        isHolidayUpdated,
        openDeleteModal,
        setOpenDeleteModal,
        confirmDeleteHoliday,
        isRefused: holiday?.state_id === HolidayState.REFUSED,
        isCancelled: holiday?.state_id === HolidayState.CANCELLED
    };
};
